import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { Divider, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { CoreCard, CoreChip, CoreRadioGroup, OverflowText, RadioOption } from "@verity/components/core";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MultipleChoiceIcon from "../../../../images/quiz-question/question-type-icon-multiple-choice.svg?react";
import TrueFalseIcon from "../../../../images/quiz-question/question-type-icon-true-false.svg?react";
import FillInTheBlankIcon from "../../../../images/quiz-question/question-type-icon-fill-in-the-blank.svg?react";
import MatchingIcon from "../../../../images/quiz-question/question-type-icon-matching.svg?react";
import CorrectIcon from "../../../../images/quiz-question/option-correct-answer-checked-icon.svg?react";
import InCorrectIcon from "../../../../images/quiz-question/option-inccorect-icon.svg?react";
import CorrectStatusIcon from "../../../../images/quiz-question/question-status-correct-icon.svg?react";
import InCorrectStatusIcon from "../../../../images/quiz-question/question-status-incorrect-icon.svg?react";
import PartialStatusIcon from "../../../../images/quiz-question/question-status-partial-correct-icon.svg?react";
import UnansweredStatusIcon from "../../../../images/quiz-question/question-status-unanswered-icon.svg?react";

import i18n from "../../../../i18n.js";
import styles from "./QuizResultPanel.module.css";
import {
	ChoiceAnswer,
	FITBAnswer,
	FITBBlankOptionInstructor,
	MatchingAnswer,
	MatchingPairInstructor,
	MCOptionInstructor,
	TFOptionInstructor,
} from "@verity/api-client/types";
import { UseVerityAssessmentResultReturn } from "./hooks/useVerityAssessmentResult.js";
import {
	DraftGrade,
	getGradeDraftFromResponse,
	getMaxGradeForQuestion,
	isGradeable,
	QuizQuestion,
	QuizResponseResult,
} from "./hooks/verityAssessmentResultHelper.js";
import { GradeInput, NotesTextField } from "./tabs/AssignmentPanel.js";

type QuizResultPanelProps = Pick<UseVerityAssessmentResultReturn, "quizQuestions" | "quizResponses" | "grade">;

type ScoreStatus = "correct" | "partial" | "incorrect" | "unanswered";

function getScoreStatus(scored: number, total: number, isAnswered: boolean): ScoreStatus {
	if (!isAnswered) return "unanswered";
	if (scored >= total) return "correct";
	if (scored > 0) return "partial";
	return "incorrect";
}

// ── FITB segment parser ───────────────────────────────────────────────────────

type FITBSegment = { type: "html"; content: string } | { type: "blank"; blankId: number };

function parseFITBSegments(html: string): FITBSegment[] {
	if (typeof document === "undefined") return [{ type: "html", content: html }];
	const div = document.createElement("div");
	div.innerHTML = DOMPurify.sanitize(html, { ADD_ATTR: ["data-id"], ADD_TAGS: ["span"] });
	const PLACEHOLDER = "__FITB_BLANK_";
	div.querySelectorAll(".question-blank").forEach((span) => {
		const blankId = span.getAttribute("data-id") ?? "0";
		span.replaceWith(document.createTextNode(`${PLACEHOLDER}${blankId}__`));
	});
	const result: FITBSegment[] = [];
	const root: Element =
		div.children.length === 1 && div.firstElementChild?.tagName === "P" ? (div.firstElementChild as HTMLElement) : div;
	let remaining = root.innerHTML;
	const regex = new RegExp(`${PLACEHOLDER}(\\d+)__`);
	while (remaining.length > 0) {
		const match = regex.exec(remaining);
		if (!match) {
			result.push({ type: "html", content: remaining });
			break;
		}
		if (match.index > 0) result.push({ type: "html", content: remaining.slice(0, match.index) });
		result.push({ type: "blank", blankId: parseInt(match[1]) });
		remaining = remaining.slice(match.index + match[0].length);
	}
	return result;
}

function QuestionTypeIcon({ type }: { type: QuizQuestion["questionType"] }) {
	switch (type) {
		case "MultipleChoice":
			return <MultipleChoiceIcon />;
		case "TrueFalse":
			return <TrueFalseIcon />;
		case "FillInTheBlank":
			return <FillInTheBlankIcon />;
		case "Matching":
			return <MatchingIcon />;
	}
}

function StatusIcon({ status }: { status: ScoreStatus }) {
	switch (status) {
		case "correct":
			return <CorrectStatusIcon />;
		case "partial":
			return <PartialStatusIcon />;
		case "incorrect":
			return <InCorrectStatusIcon />;
		case "unanswered":
			return <UnansweredStatusIcon />;
	}
}

function FITBQuestionText({ html }: { html: string }) {
	const segments = useMemo(() => parseFITBSegments(html), [html]);
	return (
		<div className={styles.fitbInlineContainer}>
			{segments.map((seg, i) =>
				seg.type === "html" ? (
					<span key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seg.content) }} />
				) : (
					<span key={i} className={styles.fitbBlankPlaceholder}>
						______
					</span>
				),
			)}
		</div>
	);
}

function MCAnswerView({ question, response }: { question: QuizQuestion; response: QuizResponseResult | undefined }) {
	const options = (question.options as MCOptionInstructor[] | TFOptionInstructor[] | undefined) ?? [];
	const selectedIndices: ChoiceAnswer = response?.isAnswered ? ((response.answer as ChoiceAnswer) ?? []) : [];

	return (
		<div className={styles.optionList}>
			{options.map((opt, idx) => {
				const isSelected = selectedIndices.includes(idx);
				const isCorrect = opt.weightage > 0;
				return (
					<div
						key={idx}
						className={`${styles.option} ${isSelected && isCorrect ? styles.correct : ""} ${isSelected && !isCorrect ? styles.incorrect : ""}`}
					>
						<div
							className={`${styles.optionIndexBox} ${isSelected && isCorrect ? styles.correct : ""} ${isSelected && !isCorrect ? styles.incorrect : ""}`}
						>
							{isSelected && isCorrect && <CorrectIcon />}
							{isSelected && !isCorrect && <InCorrectIcon />}
						</div>
						<OverflowText
							variant="body2"
							fontWeight={400}
							fontSize="14px"
							style={{
								flexGrow: 1,
								minWidth: 0,
							}}
						>
							{opt.text}
						</OverflowText>
					</div>
				);
			})}
		</div>
	);
}

function FITBAnswerView({ question, response }: { question: QuizQuestion; response: QuizResponseResult | undefined }) {
	const blanks = (question.options as FITBBlankOptionInstructor[] | undefined) ?? [];
	const userAnswers: FITBAnswer = (response?.answer as FITBAnswer) ?? {};
	const segments = useMemo(() => parseFITBSegments(question.questionText), [question.questionText]);

	return (
		<div className={styles.fitbInlineContainer}>
			{segments.map((seg, i) => {
				if (seg.type === "html") {
					return <span key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seg.content) }} />;
				}
				const blank = blanks.find((b) => b.blankId === seg.blankId);
				const userText = (userAnswers[seg.blankId] ?? "").trim();
				const isAnswered = response?.isAnswered === true;
				const isUnanswered = !userText;
				const isCorrect =
					!isUnanswered &&
					isAnswered &&
					(blank?.answers.some((a) => a.trim().toLowerCase() === userText.toLowerCase()) ?? false);
				return (
					<span
						key={i}
						className={`${styles.fitbInlineBlank} ${isUnanswered ? "" : isCorrect ? styles.correct : styles.incorrect}`}
					>
						<span
							className={`${styles.fitbInlineBlankIcon} ${isUnanswered ? "" : isCorrect ? styles.correct : styles.incorrect}`}
						>
							{isUnanswered ? (
								<UnansweredStatusIcon style={{ width: "16px" }} />
							) : isCorrect ? (
								<CorrectIcon />
							) : (
								<InCorrectIcon />
							)}
						</span>

						<OverflowText
							className={styles.fitbInlineBlankText}
							variant="body2"
							fontWeight={400}
							fontSize="14px"
							style={{
								maxWidth: "100px",
							}}
						>
							{userText || i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.unanswered")}
						</OverflowText>
					</span>
				);
			})}
		</div>
	);
}

function MatchingAnswerView({
	question,
	response,
}: {
	question: QuizQuestion;
	response: QuizResponseResult | undefined;
}) {
	const pairs = (question.options as MatchingPairInstructor[] | undefined) ?? [];
	const userAnswers: MatchingAnswer = (response?.answer as MatchingAnswer) ?? {};

	return (
		<div className={styles.matchingPairGridyLayout}>
			{pairs.map((pair, idx) => {
				const userResponse = userAnswers[pair.premise] ?? "";
				const isUnanswered = !userResponse;
				const isCorrect = !isUnanswered && userResponse === pair.response;
				const responseClass = `${styles.option} ${!isUnanswered && isCorrect ? styles.correct : ""} ${!isUnanswered && !isCorrect ? styles.incorrect : ""}`;
				const indexClass = `${styles.optionIndexBox} ${!isUnanswered && isCorrect ? styles.correct : ""} ${!isUnanswered && !isCorrect ? styles.incorrect : ""}`;
				return (
					<Fragment key={idx}>
						<div className={styles.option}>
							<div className={styles.optionIndexBox}>{idx + 1}</div>
							<OverflowText
								variant="body2"
								fontWeight={400}
								fontSize="14px"
								style={{
									flexGrow: 1,
									minWidth: 0,
								}}
							>
								{pair.premise}
							</OverflowText>
						</div>
						<div className={responseClass}>
							<div className={indexClass}>
								{!isUnanswered && isCorrect && <CorrectIcon />}
								{!isUnanswered && !isCorrect && <InCorrectIcon />}
							</div>
							<OverflowText
								variant="body2"
								fontWeight={400}
								fontSize="14px"
								style={{
									flexGrow: 1,
									minWidth: 0,
								}}
							>
								{userResponse || i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.unanswered")}
							</OverflowText>
						</div>
					</Fragment>
				);
			})}
		</div>
	);
}

type GradeSectionProps = {
	responseId: number;
	grade: DraftGrade;
} & Pick<QuestionDetailProps, "question" | "grade" | "onChange">;

function GradeSection({ grade, question, onChange, responseId }: GradeSectionProps) {
	const maxGrade = getMaxGradeForQuestion(question);
	const id = useId();

	const radioOptions: RadioOption[] = [
		{
			value: String(maxGrade),
			label: i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.correct", {
				points: maxGrade,
			}) as string,
		},
		{
			value: "0",
			label: i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.incorrect") as string,
		},
	];

	const handleGradeChange = (value: string) => onChange(responseId, "score", value);

	return (
		<div className={styles.gradeSection}>
			<div className={styles.gradeHeader}>
				<Typography id={id} className={styles.sectionLabel}>
					{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.gradeTitle")}
				</Typography>
				<Typography className={styles.gradeDescription}>
					{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.gradeDescription")}
				</Typography>
			</div>
			<div className={styles.gradeInputWrapper}>
				<CoreRadioGroup
					radioOptions={radioOptions}
					radioGroupAriaLabelledby={id}
					onChange={(_, value) => handleGradeChange(value)}
					selectedValue={grade.score}
					className={styles.gradeRadioGroup}
				/>
				<GradeInput
					ariaLabelledBy={id}
					value={grade.score}
					maxGrade={maxGrade}
					onChange={handleGradeChange}
					error={grade.error}
				/>
			</div>
			<NotesTextField value={grade.notes} onChange={(value) => onChange(responseId, "notes", value)} />
		</div>
	);
}

type QuestionDetailProps = {
	question: QuizQuestion;
	response: QuizResponseResult | undefined;
	index: number;
	grade: DraftGrade | undefined;
} & Pick<QuizResultPanelProps["grade"], "onChange">;

function QuestionDetail({ question, response, index, grade, onChange }: QuestionDetailProps) {
	const status = getScoreStatus(response?.score ?? 0, question.correctionPoints, response?.isAnswered ?? false);
	const typeLabel = (
		question.questionType === "FillInTheBlank"
			? i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.FillInTheBlank.label")
			: question.questionType === "TrueFalse"
				? i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.TrueFalse.label")
				: question.questionType === "Matching"
					? i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.Matching.label")
					: i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.MultipleChoice.label")
	) as string;

	const scoreColor =
		status === "unanswered"
			? "#e6e6e6"
			: status === "correct"
				? "#dbe7e1"
				: status === "partial"
					? "#faefdd"
					: "#f8dcd8";

	const t = (key: string, opts?: Record<string, unknown>) =>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(i18n.t as any)(`result.components.resultsPage.proctoringResultsViewTSX.questions.${key}`, opts);

	const renderAnswers = () => {
		switch (question.questionType) {
			case "MultipleChoice":
			case "TrueFalse":
				return <MCAnswerView question={question} response={response} />;
			case "FillInTheBlank":
				return <FITBAnswerView question={question} response={response} />;
			case "Matching":
				return <MatchingAnswerView question={question} response={response} />;
		}
	};

	const renderCorrectAnswer = () => {
		if (status === "correct") return null;
		switch (question.questionType) {
			case "MultipleChoice":
			case "TrueFalse": {
				const options = (question.options as MCOptionInstructor[] | TFOptionInstructor[] | undefined) ?? [];
				const correct = options.map((opt, idx) => ({ opt, idx })).filter(({ opt }) => opt.weightage > 0);
				if (correct.length === 0) return null;
				return (
					<div className={styles.correctAnswerSection}>
						<Typography className={styles.sectionLabel}>{t("correctAnswer")}</Typography>
						<div className={styles.correctAnswerList}>
							{correct.map(({ opt, idx }) => (
								<Typography key={idx} className={styles.correctAnswerItem}>
									{String.fromCharCode(65 + idx)}. {opt.text}
								</Typography>
							))}
						</div>
					</div>
				);
			}
			case "FillInTheBlank": {
				const blanks = (question.options as FITBBlankOptionInstructor[] | undefined) ?? [];
				if (blanks.length === 0) return null;
				const sorted = [...blanks].sort((a, b) => a.blankId - b.blankId);
				return (
					<div className={styles.correctAnswerSection}>
						<Typography className={styles.sectionLabel}>{t("correctAnswer")}</Typography>
						<div className={styles.correctAnswerList}>
							{sorted.map((blank, i) => (
								<Typography key={blank.blankId} className={styles.correctAnswerItem}>
									{t("blankLabel", { n: i + 1 })} {!blank.answers ? "" : blank.answers.join("/")}
								</Typography>
							))}
						</div>
					</div>
				);
			}
			case "Matching": {
				const pairs = (question.options as MatchingPairInstructor[] | undefined) ?? [];
				if (pairs.length === 0) return null;
				return (
					<div className={styles.correctAnswerSection}>
						<Typography className={styles.sectionLabel}>{t("correctAnswer")}</Typography>
						<div className={styles.correctAnswerList}>
							{pairs.map((pair, idx) => (
								<Typography key={idx} className={styles.correctAnswerItem}>
									{pair.premise} → {pair.response}
								</Typography>
							))}
						</div>
					</div>
				);
			}
		}
	};

	return (
		<CoreCard className={styles.detail} slotProps={{ bodyWrapper: { className: styles.questionDetailWrapper } }}>
			<div className={styles.detailHeader}>
				<div className={styles.detailHeaderLeft}>
					<Typography className={styles.questionTitle}>{t("questionLabel", { n: index + 1 })}</Typography>
					<Typography className={styles.questionTypeLabel}>{typeLabel}</Typography>
				</div>
				<div className={styles.spacer} />

				<CoreChip chipColor="#f8e4dd" variant="rectangular" sx={{ minWidth: "unset" }}>
					{t("pointsLabel", { count: question.correctionPoints })}
				</CoreChip>
				<CoreChip chipColor={scoreColor} variant="rectangular" sx={{ minWidth: "unset" }}>
					{t("scoredLabel", { count: response?.score ?? 0 })}
				</CoreChip>
			</div>

			<div className={styles.questionTextWrapper}>
				<Typography className={styles.sectionLabel}>{t("questionSection")}</Typography>
				{question.questionType === "FillInTheBlank" ? (
					<FITBQuestionText html={question.questionText} />
				) : (
					<Typography
						className={styles.questionText}
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.questionText) }}
					/>
				)}
			</div>

			<div className={styles.answerSection}>
				<Typography className={styles.sectionLabel} sx={{ marginBottom: "8px" }}>
					{t("answerSection")}
				</Typography>
				{renderAnswers()}
				{renderCorrectAnswer()}
				{response && isGradeable(question) && grade && (
					<>
						<Divider />
						<GradeSection grade={grade} onChange={onChange} question={question} responseId={response.id} />
					</>
				)}
			</div>
		</CoreCard>
	);
}

export function QuizResultPanel({ quizQuestions, quizResponses, grade }: QuizResultPanelProps) {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const navRef = useRef<HTMLDivElement>(null);
	const [showNavBtns, setShowNavBtns] = useState(false);

	// Reset to first question whenever a new session's questions are loaded
	useEffect(() => {
		setSelectedIndex(0);
	}, [quizQuestions]);

	// Show/hide scroll buttons depending on whether content overflows
	useEffect(() => {
		const el = navRef.current;
		if (!el) return;
		const observer = new ResizeObserver(() => {
			setShowNavBtns(el.scrollWidth > el.clientWidth);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [quizQuestions]);

	const scrollNav = (direction: "left" | "right") => {
		navRef.current?.scrollBy({ left: direction === "left" ? -150 : 150, behavior: "smooth" });
	};

	if (quizQuestions.length === 0) {
		return (
			<div className={styles.emptyState}>
				<Typography variant="body2" color="text.secondary">
					{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.emptyState")}
				</Typography>
			</div>
		);
	}

	const selectedQuestion = quizQuestions[selectedIndex];
	const selectedResponse = quizResponses.find((r) => r.questionNo === selectedIndex + 1);
	const selectedGrade = getGradeDraftFromResponse(grade.drafts, selectedResponse);

	return (
		<div className={styles.panel}>
			{/* Question navigator bar */}
			<div className={styles.navBar}>
				{showNavBtns && (
					<button
						className={styles.navBtn}
						onClick={() => scrollNav("left")}
						aria-label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.scrollLeft") as string}
					>
						<ChevronLeftIcon fontSize="small" />
					</button>
				)}
				<div ref={navRef} className={styles.navScroll}>
					{quizQuestions.map((q, idx) => {
						const resp = quizResponses.find((r) => r.questionNo === idx + 1);
						const status = getScoreStatus(resp?.score ?? 0, q.correctionPoints, resp?.isAnswered ?? false);
						const isSelected = idx === selectedIndex;
						return (
							<button
								key={idx}
								className={`${styles.navItem}${isSelected ? ` ${styles.active}` : ""}`}
								onClick={() => setSelectedIndex(idx)}
								aria-label={
									i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.navItemAriaLabel", {
										n: idx + 1,
									}) as string
								}
								aria-pressed={isSelected}
							>
								<div className={styles.navIconWrapper}>
									<QuestionTypeIcon type={q.questionType} />

									<div className={styles.navStatusOverlay}>
										<StatusIcon status={status} />
									</div>
								</div>
								<Typography className={styles.navLabel}>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.navLabel", { n: idx + 1 })}
								</Typography>
							</button>
						);
					})}
				</div>
				{showNavBtns && (
					<button
						className={styles.navBtn}
						onClick={() => scrollNav("right")}
						aria-label={
							i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.scrollRight") as string
						}
					>
						<ChevronRightIcon fontSize="small" />
					</button>
				)}
			</div>

			{/* Question detail */}
			<div className={styles.detailScroll}>
				{selectedQuestion != null && (
					<QuestionDetail
						question={selectedQuestion}
						response={selectedResponse}
						index={selectedIndex}
						grade={selectedGrade}
						onChange={grade.onChange}
					/>
				)}
			</div>
		</div>
	);
}
